import { format } from "date-fns";
import { TimeEntry, TimeEntryTypes } from "models/TimeEntry";
import { calculateWorkHours, calculateOvertimePercentages } from "utilities/Conversions";
import { absenceTypeToString } from "utilities/formatAbsence";
import { absenceStateToString } from "utilities/formatAbsence";

interface CsvRow {
  name: string;
  from: string;
  to: string;
  type: string;
  comment: string;
  status: string;
  processed: string;
  workHours: string;
  overtimeFifty: string;
  overtimeHundred: string;
};

const COLUMNS = [
  {
    id: "name",
    displayName: "Namn",
  },
  {
    id: "anstNr",
    displayName: "Anst.nr",
  },
  {
    id: "from",
    displayName: "Från",
  },
  {
    id: "to",
    displayName: "Till",
  },
  {
    id: "type",
    displayName: "Typ",
  },
  {
    id: "comment",
    displayName: "Kommentar",
  },
  {
    id: "status",
    displayName: "Status",
  },
  {
    id: "processed",
    displayName: "Behandlad av ekonomi",
  },
  {
    id: "workHours",
    displayName: "Total arbetstid",
  },
  {
    id: "overtimeFifty",
    displayName: "Övertid 50%",
  },
  {
    id: "overtimeHundred",
    displayName: "Övertid 100%",
  },
  {
    id: "workHoursWithinInterval",
    displayName: "Total arbetstid inom vald period",
  },
] as const;

const formatHeaders = (columns: typeof COLUMNS): string => {
  return columns.map(col => col.displayName).join(";");
}

const formatRow = (data: TimeEntry): string => {
  const overtime = (data.type === TimeEntryTypes.Overtime)
    ? calculateOvertimePercentages(new Date(data.from), new Date(data.to)) : undefined;

  const row = {
    name: data.userName,
    anstNr: data.userHogiaNumber || "",
    from: data.from,
    to: data.to,
    type: `${absenceTypeToString(data.type)}`,
    // Sanitize comment by removing any line breaks
    comment: data.message?.replace(/(\r\n|\n|\r)/gm, "").toString() || "",
    status: absenceStateToString(data.state),
    processed: data.processed ? "Ja" : "Nej",
    workHours:
      data.type !== TimeEntryTypes.Overtime
        ? calculateWorkHours(
          new Date(data.from),
          new Date(data.to)
        ).toFixed(2)
        : "",
    overtimeFifty: overtime ? overtime[0].toFixed(2) : "",
    overtimeHundred: overtime ? overtime[1].toFixed(2) : "",
  } as CsvRow;

  return Object.values(row).join(";");
}

const dataToCsvString = (data: TimeEntry[]): string => {
  return [
    formatHeaders(COLUMNS),
    ...data.map((item) => formatRow(item))].join("\n");
};

export const downloadCsv = async (data: TimeEntry[], start: Date, end: Date) => {
  // Sort by userHogiaNumber, then by from date,
  // because this is how payroll wants to process the entries
  const sorted = [...data].sort((a, b) => {
    if (a.userHogiaNumber === b.userHogiaNumber) {
      return new Date(a.from).getTime() - new Date(b.from).getTime();
    }
    return (parseInt(a.userHogiaNumber || "0") - parseInt(b.userHogiaNumber || "0"));
  });

  const blob = new Blob(["\ufeff" + dataToCsvString(sorted)], { type: "text/csv" });
  const dataUrl = URL.createObjectURL(blob);

  const element = document.createElement("a");
  element.href = dataUrl;
  element.download = `Tidrapport_${format(start, "d-L-y")}-${format(end, "d-L-y")}.csv`;
  element.click();

  URL.revokeObjectURL(dataUrl);
};
